/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import fileDownload from 'js-file-download';
import {
  Box,
  Button,
  DialogContentText,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import { AnexoPreviewV2, CustomFilterV2, DialogDefaultV2, TableV2 } from '../../../components/v2';
import { renderStatus } from '../../../components/v2/cell-renderers/status';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as Role from '../../../constants/role';
import BaseService from '../../../services/BaseService';
import Formulario from './Formulario';
import ResumoCards from './ResumoCards';

const defaultFilter = {
  inicio: moment().subtract(1, 'year').startOf('year').valueOf(),
  fim: moment().endOf('day').valueOf(),
};

const validateLabel = validate => {
  if (validate === 'INVALIDADO') return 'Invalidado';
  if (validate === 'VALIDADO') return 'Validado';
  return 'Aguardando Validação';
};

const normalizeValidateStatus = validate => {
  if (validate === null || validate === 'AGUARDANDO_VALIDACAO') {
    return 'AGUARDANDO_VALIDACAO';
  }
  return validate;
};

const OdometrosV2 = ({
  userRole,
}) => {
  const [open, setOpen] = useState(false);
  const [id, setId] = useState(null);
  const [openValidate, setOpenValidate] = useState(false);
  const [validateData, setValidateData] = useState({});
  const [localFilter, setLocalFilter] = useState(defaultFilter);
  const [openNotFotoModal, setOpenNotFotoModal] = useState(false);
  const [openAnexoPreview, setOpenAnexoPreview] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [openExport, setOpenExport] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [reload, setReload] = useState(false);
  const [activeStatus, setActiveStatus] = useState(null);

  const isAdmin = () => Role.ADMIN === userRole || Role.SUPER_ADMIN === userRole;

  const apiFilterBase = useMemo(() => {
    const rest = { ...localFilter };
    delete rest.validate;
    return rest;
  }, [localFilter]);

  const apiFilter = useMemo(() => {
    if (activeStatus != null) {
      return { ...localFilter, validate: activeStatus };
    }
    return { ...localFilter };
  }, [localFilter, activeStatus]);

  const findFotoById = async rowId => {
    try {
      const response = await BaseService.get({ url: `odometros/${rowId}/arquivos` });
      if (response.data && response.data.length > 0) {
        setAttachments(response.data.map((file) => ({
          nome: file.nome || file.title,
          title: file.title || file.nome,
          extensao: file.extensao,
          downloadUrl: file.url,
        })));
        setOpenAnexoPreview(true);
      } else {
        setOpenNotFotoModal(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const onValidateOdometro = async () => {
    try {
      await BaseService.post({
        url: 'odometros/validate',
        data: { id: validateData.id, validate: validateData.validate },
      });
      setReload(prev => !prev);
    } catch (error) {
      console.error(error);
    } finally {
      setOpenValidate(false);
    }
  };

  const onExportOdometro = async () => {
    try {
      const response = await BaseService.execute({
        url: 'odometros/export',
        data: apiFilter,
        method: 'POST',
        options: {
          responseType: 'blob',
        },
      });
      fileDownload(response.data, 'Relatório Odômetro.xlsx');
      setOpenExport(false);
    } catch (error) {
      console.error(error);
    }
  };

  const onEdit = rowId => {
    setOpen(true);
    setId(rowId);
  };

  const onAdd = () => {
    setOpen(true);
    setId(null);
  };

  const onClose = isSave => {
    if (isSave) {
      setReload(prev => !prev);
    }
    setOpen(false);
    setId(null);
  };

  const columns = [
    {
      field: 'data',
      headerName: 'Data',
      minWidth: 120,
      flex: 0.8,
      renderCell: params => moment(Number(params.row.data)).format('DD/MM/YYYY'),
    },
    { field: 'veiculoPlaca', headerName: 'Veículo', minWidth: 120, flex: 1 },
    { field: 'odometroInicial', headerName: 'Inicial', minWidth: 110, flex: 0.8 },
    { field: 'odometroFinal', headerName: 'Final', minWidth: 110, flex: 0.8 },
    { field: 'usuarioNome', headerName: 'Usuário', minWidth: 180, flex: 1.2 },
    { field: 'inspetorAuxiliarNome', headerName: 'Observador/Ajudante', minWidth: 190, flex: 1.2 },
    {
      field: 'validate',
      headerName: 'Validação',
      minWidth: 160,
      flex: 1,
      renderCell: params =>
        renderStatus({
          value: normalizeValidateStatus(params.row.validate),
          label: validateLabel(params.row.validate),
        }),
    },
  ];

  const actions = [
    {
      key: 'view',
      title: 'Ver fotos',
      icon: <AttachFileIcon fontSize="small" />,
      onClick: findFotoById,
    },
    {
      key: 'validate',
      title: 'Validar',
      icon: <CheckCircleOutlineIcon fontSize="small" />,
      show: row => isAdmin() && (row.validate === null || row.validate === 'AGUARDANDO_VALIDACAO'),
      onClick: rowId => {
        setValidateData({
          id: rowId,
          validate: 'VALIDADO',
          message: 'Deseja validar o odômetro?',
        });
        setOpenValidate(true);
      },
    },
    {
      key: 'invalidate',
      title: 'Invalidar',
      icon: <NotInterestedIcon fontSize="small" />,
      show: row => isAdmin() && (row.validate === null || row.validate === 'AGUARDANDO_VALIDACAO'),
      onClick: rowId => {
        setValidateData({
          id: rowId,
          validate: 'INVALIDADO',
          message: 'Deseja invalidar o odômetro?',
        });
        setOpenValidate(true);
      },
    },
  ];

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ pr: 2 }}
        >
          <Typography variant="h4">Odômetros</Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        <ResumoCards
          apiFilterBase={apiFilterBase}
          activeStatus={activeStatus}
          onFilterByStatus={setActiveStatus}
          refreshKey={reload}
        />

        <TableV2
          useApi
          fetchUrl="odometros/filtro"
          paginationUrl="/v2/odometros"
          filterData={apiFilter}
          refreshKey={reload}
          rowHeight={70}
          pageSize={10}
          headerActions={[
            { label: 'Novo', onClick: onAdd, startIcon: <AddIcon />, variant: 'contained' },
            {
              label: 'Exportar',
              onClick: () => setOpenExport(true),
              startIcon: <DescriptionOutlinedIcon />,
              variant: 'outlined',
            },
          ]}
          actions={actions}
          columns={columns}
          onEdit={onEdit}
          emptyMessage="Nenhum odômetro encontrado"
        />

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          defaultFilter={defaultFilter}
          appliedFilter={localFilter}
          fields={[
            { field: 'useDates' },
            { field: 'useInspetor', md: 6, xs: 12 },
            { field: 'useVeiculo', key: 'veiculoIds', md: 6, xs: 12 },
            { field: 'useValidate', md: 6, xs: 12 },
          ]}
          onFilter={filterData => {
            setLocalFilter(filterData);
            setOpenFilter(false);
          }}
        />

        <Formulario id={id} open={open} onClose={onClose} isAdmin={isAdmin()} />

        <DialogDefaultV2
          open={openValidate}
          handlerCancel={() => {
            setValidateData({});
            setOpenValidate(false);
          }}
          handlerConfirm={onValidateOdometro}
          title="Confirmação"
          confirmMessage="Sim"
          cancelMessage="Não"
        >
          <DialogContentText>{validateData.message}</DialogContentText>
        </DialogDefaultV2>

        <AnexoPreviewV2
          open={openAnexoPreview}
          onClose={() => {
            setOpenAnexoPreview(false);
            setAttachments([]);
          }}
          attachments={attachments}
          title="Fotos do odômetro"
          lazyLoad
        />

        <DialogDefaultV2
          open={openNotFotoModal}
          handlerCancel={() => setOpenNotFotoModal(false)}
          handlerConfirm={() => setOpenNotFotoModal(false)}
          readOnly
        >
          <DialogContentText>Odômetro não possui foto</DialogContentText>
        </DialogDefaultV2>

        <DialogDefaultV2
          open={openExport}
          handlerCancel={() => setOpenExport(false)}
          handlerConfirm={onExportOdometro}
          title="Confirmação"
          confirmMessage="Sim"
          cancelMessage="Não"
        >
          <DialogContentText>Deseja baixar todas as informações filtradas?</DialogContentText>
        </DialogDefaultV2>
      </Stack>
    </Box>
  );
};

OdometrosV2.propTypes = {
  userRole: PropTypes.string.isRequired,
};

const mapStateToProps = state => ({
  userRole: selectorsAuth.getRole(state),
});

export default connect(mapStateToProps)(withRouter(OdometrosV2));
